import {computed, inject, Injectable} from '@angular/core';
import {Profile} from '@models';
import {ProfileService} from './profile.service';

export interface ProfileCompletionItem {
	readonly key: string;
	readonly weight: number;
	readonly completed: boolean;
}

export interface ProfileCompletionResult {
	readonly percentage: number;
	readonly color: string;
	readonly items: ProfileCompletionItem[];
}

@Injectable({providedIn: 'root'})
export class ProfileCompletionQueryService {
	private readonly profileService = inject(ProfileService);

	readonly completion = computed<ProfileCompletionResult>(() => this.getCompletion(this.profileService.profile()));

	getCompletion(profile: Profile | undefined): ProfileCompletionResult {
		const items: ProfileCompletionItem[] = [
			{
				key: 'photo',
				weight: 15,
				completed: !!profile && profile.hasImage && profile.imgUrl !== Profile.defaultImg
			},
			{
				key: 'bio',
				weight: 10,
				completed: !!profile?.shortDesc?.trim()
			},
			{
				key: 'description',
				weight: 15,
				completed: !!profile?.description?.trim()
			},
			{
				key: 'preferences',
				weight: 20,
				completed: !!profile && (profile.relation?.length ?? 0) > 0 && profile.status !== undefined
			},
			{
				key: 'identity',
				weight: 20,
				completed: !!profile && profile.role !== undefined && profile.gender !== undefined && (profile.orientation?.length ?? 0) > 0
			},
			{
				key: 'test',
				weight: 20,
				completed: !!profile?.test?.length
			}
		];

		const totalWeight = items.reduce((sum, item) => sum + item.weight, 0);
		const completedWeight = items.reduce((sum, item) => sum + (item.completed ? item.weight : 0), 0);
		const percentage = totalWeight > 0 ? Math.round((completedWeight / totalWeight) * 100) : 0;

		return {
			percentage,
			color: this.getColor(percentage),
			items
		};
	}

	private getColor(percentage: number): string {
		const clamped = Math.max(0, Math.min(100, percentage));
		const hue = (clamped / 100) * 120;
		return `hsl(${hue}, 85%, 45%)`;
	}
}
